import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config';
const API = API_BASE;

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function PasswordInput({ value, onChange, placeholder, autoComplete, style }) {
  const [visible, setVisible] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        type={visible ? 'text' : 'password'}
        value={value} onChange={onChange} required
        placeholder={placeholder} autoComplete={autoComplete}
        style={{ ...style, paddingRight: '2.5rem' }}
      />
      <button type="button" onClick={() => setVisible(v => !v)} tabIndex={-1}
        style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', display: 'flex', alignItems: 'center', padding: 0, width: 'auto' }}>
        <EyeIcon open={visible} />
      </button>
    </div>
  );
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) setError('No reset token found. Please request a new password reset link.');
  }, [token]);

  const inputStyle = {
    width: '100%', padding: '0.75rem 0.9rem', borderRadius: 8,
    border: '1.5px solid #e5e7eb', fontSize: '0.9rem', fontFamily: 'inherit',
    boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.15s',
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, newPassword: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. Please try again or request a new link.');
    }
    setLoading(false);
  }

  return (
    <div style={{
      minHeight: '100vh', background: '#f7f7f9',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem',
    }}>
      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <img src="/Logo.png" alt="GuruOne" style={{ height: 88, display: 'block', margin: '0 auto 0.75rem' }} />
        <div style={{ fontSize: '0.82rem', color: '#aaa' }}>Your virtual C-suite</div>
      </div>

      <div style={{ background: 'white', borderRadius: 16, padding: '2.5rem', width: '100%', maxWidth: 420, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>

        {!success ? (
          <>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', color: '#1a1a2e', fontWeight: 700 }}>Set a new password</h2>
            <p style={{ margin: '0 0 1.75rem', fontSize: '0.82rem', color: '#888', lineHeight: 1.55 }}>
              Choose a new password for your GuruOne account.
            </p>

            {!token ? (
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '0.82rem', color: '#991B1B', marginBottom: '1rem' }}>This reset link is invalid or missing.</p>
                <Link to="/login" style={{ color: '#1D9E75', fontWeight: 600, fontSize: '0.85rem' }}>← Back to login</Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>New password</label>
                  <PasswordInput
                    value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="At least 8 characters" autoComplete="new-password" style={inputStyle}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>Confirm new password</label>
                  <PasswordInput
                    value={confirm} onChange={e => setConfirm(e.target.value)}
                    placeholder="Repeat new password" autoComplete="new-password" style={inputStyle}
                  />
                </div>

                {error && (
                  <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#991B1B' }}>
                    {error}
                    {error.includes('expired') || error.includes('invalid') ? (
                      <div style={{ marginTop: '0.4rem' }}>
                        <Link to="/login" style={{ color: '#991B1B', fontWeight: 600 }}>Request a new link →</Link>
                      </div>
                    ) : null}
                  </div>
                )}

                <button type="submit" disabled={loading || !password || !confirm}
                  style={{
                    padding: '0.85rem', borderRadius: 8, background: '#1D9E75', color: 'white',
                    border: 'none', fontSize: '0.95rem', fontWeight: 600, width: '100%',
                    cursor: loading || !password || !confirm ? 'not-allowed' : 'pointer',
                    opacity: loading || !password || !confirm ? 0.6 : 1,
                    fontFamily: 'inherit', marginTop: '0.5rem', transition: 'opacity 0.15s',
                  }}>
                  {loading ? 'Updating password...' : 'Set new password →'}
                </button>
              </form>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✓</div>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', color: '#065f46', fontWeight: 700 }}>Password updated</h2>
            <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 1.5rem', lineHeight: 1.6 }}>
              Your password has been changed. You can now log in with your new password.
            </p>
            <button onClick={() => navigate('/login')}
              style={{ padding: '0.75rem 2rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Go to login →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
